import React, { useEffect, useMemo } from 'react';
import { Drawer, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetRouteViewQuery } from '../../store/api/routesApi';
import { useGetApplicationByIdQuery } from '../../store/api/applicationsApi';
import type { WorkspaceTask } from '../../store/api/workspaceApi';
import type { RootState } from '../../store';
import { isFinanceWorkspaceTask } from '../../domain/workspaceTaskSemantics';
import { isOpenWorkspaceTask, workspaceTaskIsOverdue } from '../../domain/dashboardTaskKpiSelectors';
import { applicationCardPath, dashboardRoleKind, routeCardPath } from '../../utils/roleAwareEntityNavigation';
import ApplicationContextDrawerPanel from './ApplicationContextDrawerPanel';
import RouteContextDrawerRoutePanel from './RouteContextDrawerRoutePanel';

export type DashboardFinanceDrawerTarget = { kind: 'route' | 'application'; id: number } | null;

interface DashboardFinanceContextDrawerProps {
  open: boolean;
  onClose: () => void;
  target: DashboardFinanceDrawerTarget;
  allTasks: WorkspaceTask[];
  onInlineTaskAction: (task: WorkspaceTask) => void;
  /** Переключить ящик на другой маршрут (из заявки). */
  onSwitchTarget?: (next: DashboardFinanceDrawerTarget) => void;
}

export const DashboardFinanceContextDrawer: React.FC<DashboardFinanceContextDrawerProps> = ({
  open,
  onClose,
  target,
  allTasks,
  onInlineTaskAction,
  onSwitchTarget,
}) => {
  const navigate = useNavigate();
  const roleName = useSelector((s: RootState) => s.auth.user?.role?.name);
  const roleKind = useMemo(() => dashboardRoleKind(roleName), [roleName]);

  const routeId = target?.kind === 'route' ? target.id : undefined;
  const applicationId = target?.kind === 'application' ? target.id : undefined;

  const {
    data: routeView,
    isLoading: routeLoading,
    isFetching: routeFetching,
  } = useGetRouteViewQuery(routeId!, { skip: !open || routeId == null });

  const {
    data: application,
    isLoading: appLoading,
    isFetching: appFetching,
  } = useGetApplicationByIdQuery(applicationId!, { skip: !open || applicationId == null });

  const loading = Boolean(
    (target?.kind === 'route' && (routeLoading || routeFetching) && !routeView) ||
      (target?.kind === 'application' && (appLoading || appFetching) && !application)
  );

  const drawerFinanceTasks = useMemo(() => {
    if (!target) return [];
    return allTasks.filter((t) => {
      if (!isFinanceWorkspaceTask(t) || !isOpenWorkspaceTask(t)) return false;
      if (target.kind === 'route') return t.routeId === target.id;
      return t.applicationId === target.id;
    });
  }, [allTasks, target]);

  const drawerOperationalTasks = useMemo(() => {
    if (!target) return [];
    return allTasks.filter((t) => {
      if (isFinanceWorkspaceTask(t) || !isOpenWorkspaceTask(t)) return false;
      if (target.kind === 'route') return t.routeId === target.id;
      return t.applicationId === target.id;
    });
  }, [allTasks, target]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!open || !target) return;
    console.debug('[PHASE_5A1][finance-context-drawer]', {
      target,
      financeTasks: drawerFinanceTasks.length,
      operationalTasks: drawerOperationalTasks.length,
    });
  }, [open, target, drawerFinanceTasks.length, drawerOperationalTasks.length]);

  const applicationDirectionLine = useMemo(() => {
    if (target?.kind === 'application' && application) {
      const lc = application.loadingCity;
      const uc = application.unloadingCity;
      const line = [lc, uc].filter(Boolean).join(' → ');
      return line || '—';
    }
    return '—';
  }, [target, application]);

  const overdueFinanceInContext = useMemo(
    () => drawerFinanceTasks.some((t) => workspaceTaskIsOverdue(t)),
    [drawerFinanceTasks]
  );

  const overdueOperationalInContext = useMemo(
    () => drawerOperationalTasks.some((t) => workspaceTaskIsOverdue(t)),
    [drawerOperationalTasks]
  );

  const applicationLinkedRoutes = useMemo(() => {
    if (target?.kind !== 'application' || !application?.routes?.length) return [];
    return application.routes.slice(0, 8);
  }, [target, application]);

  const openFullCard = () => {
    if (!target) return;
    if (target.kind === 'route') navigate(routeCardPath(target.id, roleKind));
    else navigate(applicationCardPath(target.id, roleKind));
    onClose();
  };

  const applicationHeader =
    application?.internalNumber?.trim()
      ? `Заявка ${application.internalNumber.trim()}`
      : `Заявка №${target?.id ?? ''}`;

  const drawerTitle =
    target?.kind === 'route'
      ? `Маршрут №${routeView?.operation?.id ?? target.id}`
      : applicationHeader;

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
      open={open && target != null}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { paddingTop: 12 } }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : target?.kind === 'route' && routeView ? (
        <RouteContextDrawerRoutePanel
          routeView={routeView}
          drawerOperationalTasks={drawerOperationalTasks}
          drawerFinanceTasks={drawerFinanceTasks}
          overdueFinanceInContext={overdueFinanceInContext}
          overdueOperationalInContext={overdueOperationalInContext}
          onInlineTaskAction={onInlineTaskAction}
          onClose={onClose}
          onOpenFullCard={openFullCard}
        />
      ) : target?.kind === 'application' && application ? (
        <ApplicationContextDrawerPanel
          application={application}
          directionLine={applicationDirectionLine}
          drawerOperationalTasks={drawerOperationalTasks}
          drawerFinanceTasks={drawerFinanceTasks}
          overdueFinanceInContext={overdueFinanceInContext}
          overdueOperationalInContext={overdueOperationalInContext}
          linkedRoutes={applicationLinkedRoutes}
          onInlineTaskAction={onInlineTaskAction}
          onClose={onClose}
          onOpenFullCard={openFullCard}
          onSwitchToRoute={onSwitchTarget ? (routeId) => onSwitchTarget({ kind: 'route', id: routeId }) : undefined}
          onOpenRoute={
            !onSwitchTarget
              ? (routeId) => {
                  navigate(routeCardPath(routeId, roleKind));
                  onClose();
                }
              : undefined
          }
        />
      ) : null}
    </Drawer>
  );
};

export default DashboardFinanceContextDrawer;
